import { Drawer, Modal, Spin } from 'antd';
import { Link } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import YAML from 'yaml';
import insurerGroupByTenant from '../../data/insurerGroupByTenant';
import AlertaErro from '../components/molecules/AlertaErro';
import GrupoSeguros from '../components/molecules/GrupoSeguros';
import DrawerSolicitacaoCotacao from '../components/organisms/DrawerSolicitacaoCotacao';
import CookiesProvider from '../components/organisms/CookiesProvider';
import DefaultTemplate from '../components/templates/default';
import { sendMessageToParentWindow } from '../services/parent-window-bus';
import { aceitar, buscarAceite, cancelar, tiposAceite } from '../services/repositories/cotacaoRepository';
import searchPersonalData from '../services/repositories/employeeRepository';
import clearUrlParamsWithoutRefreshPage from '../util/clearUrlParemeters';
import useEmployeeCredentials from '../util/hooks/useEmployeeCredentials';
import useSSR from '../util/hooks/useSSR';
import useVariablesFromQueryString from '../util/hooks/useVariablesFromQueryString';

function PortalSeguros() {
  useVariablesFromQueryString();

  const {
    sourceSystem,
    token,
    key,
    nomeUsuario,
    nomeCompleto,
    telefone,
    whatsapp,
    cidade,
    estado,
    email,
    dataNascimento,
    segurosAceite,
    seguro,
    tenant,
  } = useEmployeeCredentials();

  clearUrlParamsWithoutRefreshPage();
  const [gruposSeguros, setGruposSeguros] = useState(insurerGroupByTenant(tenant));

  const procurarGrupoSelecionado = () => {
    if (!gruposSeguros) return {};
    if (seguro) {
      return gruposSeguros.find((g) => g.id === seguro) || gruposSeguros[0];
    }

    return gruposSeguros[0];
  };

  const [seguroSelecionado, setSeguroSelecionado] = useState(procurarGrupoSelecionado());
  const [visibleDrawer, setVisibleDrawer] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [dadosPessoais, setDadosPessoais] = useState({
    produtoInteresse: procurarGrupoSelecionado().nome,
    sistemaOrigem: sourceSystem,
    key,
    nomeUsuario,
    nomeCompleto: nomeCompleto || '',
    telefone: telefone || '',
    whatsapp: whatsapp || '',
    cidade: cidade || '',
    estado: estado || '',
    email: email || '',
    dataNascimento: dataNascimento || '',
    areaFormacao: '',
    hospitalClinicaLaboratorio: '',
    cotacaoForaPortfolio: false,
    cotacaoCoparticipacao: false,
    dependentesPlano: [],
    desejaContatoWhatsapp: false,
    desejaContatoLigacao: true,
    feedback: '',
    enviadoComSucesso: false,
    loading: false,
    lgpdChecked: segurosAceite === 'true',
    exibeCotacao: false,
    houveAlteracao: false,
    exibeTabelaPlanos: false,
    formComponentKey: procurarGrupoSelecionado().id || 'base',
    dadosAuto: {},
    dadosResidencial: {},
    dadosPet: {},
    dadosViagem: {},
    dadosVida: {},
    dadosBike: {},
  });

  const onAlterarSeguroSelecionado = (novoSeguro) => {
    setSeguroSelecionado(novoSeguro);
    setVisibleDrawer(true);
    setDadosPessoais({
      ...dadosPessoais,
      produtoInteresse: novoSeguro.nome,
      formComponentKey: novoSeguro.id || 'base',
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);

      const dadosPessoaisProm = searchPersonalData(encodeURIComponent(token), tenant);
      const aceiteProm = buscarAceite(encodeURIComponent(token), tiposAceite.seguros, tenant);

      const [dadosPessoaisResult, aceiteResult] = await Promise.all([dadosPessoaisProm, aceiteProm]);

      setDadosPessoais({ ...dadosPessoais, ...dadosPessoaisResult, lgpdChecked: aceiteResult });

      setPageLoading(false);
    };

    if (token && tenant) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    setGruposSeguros(insurerGroupByTenant(tenant));
  }, [tenant]);

  // @TODO: Separar em 2 functions diferentes, pois são 2 operações diferentes
  const enviarAceite = async (valor) => {
    try {
      if (!valor) {
        await cancelar(tiposAceite.seguros, token, sourceSystem, tenant);

        setDadosPessoais({ ...dadosPessoais, lgpdChecked: false });

        return;
      }

      await aceitar(
        tiposAceite.seguros,
        token,
        sourceSystem,
        {
          empresaId: dadosPessoais.folha,
          ...dadosPessoais,
        },
        tenant
      );

      setDadosPessoais({
        ...dadosPessoais,
        loading: false,
        enviadoComSucesso: true,
        feedback: '',
        lgpdChecked: true,
      });

      const {
        exibeCotacao,
        houveAlteracao,
        exibeTabelaPlanos,
        loading,
        enviadoComSucesso,
        lgpdChecked,
        ...dadosAceite
      } = dadosPessoais;

      const messageWindow = {
        action: 'aceiteSeguros',
        aceiteValor: valor,
        dadosAceite: YAML.stringify(dadosAceite, 4),
      };

      sendMessageToParentWindow(messageWindow);
    } catch (err) {
      Modal.error({
        title: 'Ops, algo deu errado...',
        content: err.message,
      });
    }
  };

  const handleSolicitarCotacaoClick = async () => {
    setDadosPessoais({ ...dadosPessoais, exibeCotacao: true });
  };

  const isSSR = useSSR();

  if (isSSR) {
    return null;
  }

  if (!sourceSystem) {
    return (
      <AlertaErro mensagemErro="Não foram obtidos os parâmetros necessários para concluir a ação. Para resolver o seu caso, por favor, entre em contato com o nosso suporte." />
    );
  }

  if (!tenant) {
    return (
      <AlertaErro mensagemErro="Cooperativa de origem não encontrada. Para resolver o seu caso, por favor, entre em contato com o nosso suporte." />
    );
  }

  return (
    <>
      <Helmet>
        <title>Seguros e Benefícios Corretora Parceira</title>
      </Helmet>
      <DefaultTemplate window="Seguros e Benefícios Corretora Parceira">
        <Spin
          tip="Carregando..."
          spinning={pageLoading}
          size="large"
        >
          {/* <Portfolio /> */}

          <h2>Qual seguro você está procurando?</h2>
          {tenant && (
            <>
              <GrupoSeguros
                gruposSeguros={gruposSeguros}
                onAlterarSeguroSelecionado={onAlterarSeguroSelecionado}
              />

              <Link to="/faq">
                <span style={{ textDecoration: 'underline' }}>Dúvidas? Consulte nosso FAQ</span>
              </Link>
              <Drawer
                width={736}
                placement="right"
                closable={false}
                destroyOnClose
                onClose={() => {
                  setVisibleDrawer(false);
                  setDadosPessoais({
                    ...dadosPessoais,
                    exibeCotacao: false,
                    enviadoComSucesso: false,
                    exibeTabelaPlanos: false,
                  });
                }}
                visible={visibleDrawer}
              >
                <DrawerSolicitacaoCotacao
                  seguroSelecionado={seguroSelecionado}
                  pageLoading={pageLoading}
                  dadosPessoais={dadosPessoais}
                  enviarAceite={enviarAceite}
                  onSolicitarCotacao={handleSolicitarCotacaoClick}
                  onAlterarDadosPessoais={(dados) => setDadosPessoais(dados)}
                  onAlterarSeguroSelecionado={onAlterarSeguroSelecionado}
                />
              </Drawer>
            </>
          )}
        </Spin>
      </DefaultTemplate>
    </>
  );
}

function PortalSegurosPage() {
  return (
    <CookiesProvider>
      <PortalSeguros />
    </CookiesProvider>
  );
}

export default PortalSegurosPage;
